import React, { forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { DateRange } from '../../types/SearchFilters';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (dateRange: DateRange) => void;
  className?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  className = '',
}) => {
  const CustomInput = forwardRef<HTMLDivElement, { value?: string; onClick?: () => void }>(
    ({ value, onClick }, ref) => (
      <div
        ref={ref}
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full cursor-pointer hover:border-gray-300 transition-colors ${className}`}
      >
        <CalendarIcon className="h-5 w-5 text-gray-400" />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {value || 'Add dates'}
          </span>
          <span className="text-xs text-gray-500">
            {startDate && endDate
              ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
              : 'Check-in - Check-out'}
          </span>
        </div>
      </div>
    )
  );

  return (
    <ReactDatePicker
      selected={startDate}
      onChange={(dates) => {
        if (Array.isArray(dates)) {
          const [start, end] = dates;
          onChange({ startDate: start, endDate: end });
        }
      }}
      startDate={startDate}
      endDate={endDate}
      selectsRange
      monthsShown={2}
      minDate={new Date()}
      customInput={<CustomInput />}
      wrapperClassName={className}
      calendarClassName="shadow-xl border border-gray-200 rounded-2xl overflow-hidden"
      dateFormat="MMM d, yyyy"
      showPopperArrow={false}
      popperPlacement="bottom-start"
      popperModifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
          fn: ({ x, y }) => ({ x, y: y + 8 })
        },
      ]}
    />
  );
};

export default DateRangePicker;